/**
 * Customers Page V2 - Premium SaaS Customer Management
 * 
 * Refonte UX:
 * - Lignes enrichies avec statut, plan, facturation, alertes
 * - Actions rapides: ouvrir tenant, voir factures, suspendre
 * - Hiérarchie visuelle claire
 * - Densité optimisée
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Plus, Search, Building2, RefreshCw, Mail, Phone,
  Pause, Play, Eye, ExternalLink, FileText, AlertTriangle,
  CheckCircle, CreditCard, Calendar, ChevronRight, Clock,
  MoreVertical, XCircle, ArrowUpRight, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '../../components/ui/tooltip';
import { api } from '../../App';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, suspended: 0, trial: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchPlans();
  }, [statusFilter, planFilter]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);

      const { data } = await api.get(`/admin/customers?${params}`);
      
      // Filter by plan if needed
      let filtered = data.accounts || [];
      if (planFilter !== 'all') {
        filtered = filtered.filter(c => 
          c.subscription?.plan === planFilter || 
          c.subscription?.planDetails?.code === planFilter
        );
      }
      
      setCustomers(filtered);
      
      // Calculate stats
      setStats({
        total: data.accounts?.length || 0,
        active: data.accounts?.filter(c => c.status === 'ACTIVE').length || 0,
        suspended: data.accounts?.filter(c => c.status === 'SUSPENDED').length || 0,
        trial: data.accounts?.filter(c => c.subscription?.status === 'TRIAL').length || 0
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error loading plans');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const handleSuspend = async (customer) => {
    if (!confirm(`Suspendre le client "${customer.name}" ?`)) return;
    try {
      await api.post(`/admin/customers/${customer.id}/suspend`, { reason: 'Suspension manuelle' });
      toast.success('Client suspendu');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleReactivate = async (customer) => {
    try {
      await api.post(`/admin/customers/${customer.id}/reactivate`);
      toast.success('Client réactivé');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const getSubscriptionAlert = (customer) => {
    if (!customer.subscription) return null;
    
    const { status, end_date, trial_end } = customer.subscription;
    
    if (status === 'EXPIRED') {
      return { type: 'critical', message: 'Expiré', color: 'red' };
    }
    
    if (status === 'TRIAL' && trial_end) {
      const days = differenceInDays(new Date(trial_end), new Date());
      if (days <= 3) return { type: 'warning', message: `Essai: J-${days}`, color: 'orange' };
      if (days <= 7) return { type: 'info', message: `Essai: J-${days}`, color: 'blue' };
    }
    
    if (end_date) {
      const days = differenceInDays(new Date(end_date), new Date());
      if (days <= 0) return { type: 'critical', message: 'Expiré', color: 'red' };
      if (days <= 7) return { type: 'warning', message: `Expire: J-${days}`, color: 'orange' };
      if (days <= 30) return { type: 'info', message: `Expire: J-${days}`, color: 'blue' };
    }
    
    return null;
  };

  const openTenantRiad = (customer) => {
    // If customer has riads, open the first one
    if (customer.riads && customer.riads.length > 0) {
      const riadId = customer.riads[0].id;
      // Store current context and navigate
      localStorage.setItem('superadmin_return', window.location.pathname);
      navigate(`/dashboard?riad=${riadId}`);
    } else {
      toast.info('Ce client n\'a pas encore de riad');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Users className="w-7 h-7 text-violet-600" />
            Clients SaaS
          </h1>
          <p className="text-muted-foreground mt-1">Gestion des comptes clients de la plateforme</p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-violet-600 hover:bg-violet-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Client
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-violet-500' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Clients</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'ACTIVE' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStatusFilter('ACTIVE')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              <p className="text-sm text-muted-foreground">Actifs</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'SUSPENDED' ? 'ring-2 ring-orange-500' : ''}`}
          onClick={() => setStatusFilter('SUSPENDED')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-600">{stats.suspended}</p>
              <p className="text-sm text-muted-foreground">Suspendus</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer transition-all hover:shadow-md">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">{stats.trial}</p>
              <p className="text-sm text-muted-foreground">En Essai</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="customer-search"
            />
          </div>
        </form>
        
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les plans</SelectItem>
            {plans.map(plan => (
              <SelectItem key={plan.id} value={plan.code}>{plan.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button variant="outline" size="icon" onClick={fetchCustomers}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">Aucun client trouvé</p>
            <p className="text-muted-foreground">Essayez de modifier vos filtres</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {customers.map(customer => {
            const alert = getSubscriptionAlert(customer);
            const hasAlert = customer.status === 'SUSPENDED' || alert?.type === 'critical' || alert?.type === 'warning';
            
            return (
              <Card 
                key={customer.id} 
                className={`transition-all hover:shadow-md ${hasAlert ? 'border-l-4 border-l-orange-400' : ''}`}
                data-testid={`customer-card-${customer.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Info principale */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                        customer.type === 'CONCIERGERIE' 
                          ? 'bg-purple-100 text-purple-600' 
                          : 'bg-violet-100 text-violet-600'
                      }`}>
                        {customer.type === 'CONCIERGERIE' ? (
                          <Users className="w-7 h-7" />
                        ) : (
                          <Building2 className="w-7 h-7" />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 
                            className="font-semibold text-lg cursor-pointer hover:text-violet-600 transition-colors"
                            onClick={() => navigate(`/admin/customers/${customer.id}`)}
                          >
                            {customer.name}
                          </h3>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {customer.type === 'CONCIERGERIE' ? 'Conciergerie' : 'Propriétaire'}
                          </Badge>
                          
                          {/* Status Badge */}
                          {customer.status === 'ACTIVE' ? (
                            <Badge className="bg-green-100 text-green-700 shrink-0">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Actif
                            </Badge>
                          ) : customer.status === 'SUSPENDED' ? (
                            <Badge className="bg-orange-100 text-orange-700 shrink-0">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Suspendu
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 shrink-0">
                              <XCircle className="w-3 h-3 mr-1" />
                              {customer.status}
                            </Badge>
                          )}
                          
                          {/* Subscription Alert */}
                          {alert && (
                            <Badge className={`shrink-0 ${
                              alert.color === 'red' ? 'bg-red-100 text-red-700' :
                              alert.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              <Clock className="w-3 h-3 mr-1" />
                              {alert.message}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2 flex-wrap">
                          {customer.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" />
                              {customer.email}
                            </span>
                          )}
                          {customer.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {customer.phone}
                            </span>
                          )}
                          {customer.createdAt && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              Depuis {format(new Date(customer.createdAt), 'MMM yyyy', { locale: fr })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Middle: Métriques */}
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{customer.riad_count || 0}</p>
                        <p className="text-xs text-muted-foreground">Riads</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold">{customer.user_count || 0}</p>
                        <p className="text-xs text-muted-foreground">Users</p>
                      </div>
                      
                      {/* Plan */}
                      <div className="text-center min-w-[110px]">
                        {customer.subscription?.planDetails ? (
                          <div>
                            <Badge 
                              className="text-sm font-semibold"
                              style={{ 
                                backgroundColor: `${customer.subscription.planDetails.color}20`,
                                color: customer.subscription.planDetails.color
                              }}
                            >
                              {customer.subscription.planDetails.name}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {customer.subscription.planDetails.price} MAD/mois
                            </p>
                          </div>
                        ) : customer.subscription?.plan ? (
                          <Badge variant="secondary">{customer.subscription.plan}</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Sans plan</Badge>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions rapides */}
                    <div className="flex items-center gap-2 shrink-0">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/customers/${customer.id}`);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Voir détails</TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/billing?account=${customer.id}`);
                              }}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Voir factures</TooltipContent>
                        </Tooltip>
                        
                        {customer.riad_count > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTenantRiad(customer);
                                }}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Ouvrir tenant</TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/customers/${customer.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Voir détails
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/admin/billing?account=${customer.id}`)}>
                            <FileText className="w-4 h-4 mr-2" />
                            Voir factures
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {customer.status === 'ACTIVE' ? (
                            <DropdownMenuItem 
                              onClick={() => handleSuspend(customer)}
                              className="text-orange-600"
                            >
                              <Pause className="w-4 h-4 mr-2" />
                              Suspendre
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleReactivate(customer)}
                              className="text-green-600"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Réactiver
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau Client SaaS</DialogTitle>
            <DialogDescription>
              Créer un nouveau compte client sur la plateforme
            </DialogDescription>
          </DialogHeader>
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              try {
                await api.post('/admin/customers', {
                  name: formData.get('name'),
                  email: formData.get('email'),
                  phone: formData.get('phone'),
                  type: formData.get('type'),
                  plan_code: formData.get('plan')
                });
                toast.success('Client créé');
                setShowCreateDialog(false);
                fetchCustomers();
              } catch (error) {
                toast.error(error.response?.data?.error || 'Erreur');
              }
            }}
            className="space-y-4"
          >
            <div>
              <Label>Nom du compte *</Label>
              <Input name="name" required placeholder="Mon Riad SARL" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input name="email" type="email" required placeholder="contact@monriad.ma" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input name="phone" placeholder="+212 6 00 00 00 00" />
            </div>
            <div>
              <Label>Type *</Label>
              <Select name="type" defaultValue="OWNER">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OWNER">Propriétaire</SelectItem>
                  <SelectItem value="CONCIERGERIE">Conciergerie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan</Label>
              <Select name="plan">
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.id} value={plan.code}>{plan.name} - {plan.price} MAD</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
